import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { ArrowUpRight, Camera, Image as ImageIcon, FileText, PenTool, TrendingUp, BarChart3, Sparkles, CheckCircle2, Star, Flame, Pin } from "lucide-react";
import SEO from "../components/SEO";
import AdSlot from "../components/AdSlot";
import useAds from "../hooks/useAds";

const iconMap = {
  TrendingUp, BarChart3, Camera, Image: ImageIcon, FileText, PenTool, Sparkles,
};

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c713bebc-7311-4fae-ba18-1f88334beafc/images/42f664fcb8268d2336d84563d5531f75a9151e92674310b67a227314dc4d9db1.png";

const DEFAULT_STATS = [
  { value: "240+", label: "Restaurants boosted" },
  { value: "3.2x", label: "Avg. order growth" },
  { value: "92%", label: "Menu score uplift" },
  { value: "48h", label: "Avg. turnaround" },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [hotTitle, setHotTitle] = useState(null);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const { hasAds: hasSidebarAds } = useAds("home", "sidebar");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/services/hot").then((r) => setHotTitle(r.data?.title || null)).catch(() => {});
    api.get("/portfolio").then((r) => {
      const items = (r.data || []).filter((p) => p.featured).slice(0, 3);
      setFeatured(items);
    }).catch(() => {});
    api.get("/site/hero-stats").then((r) => {
      const s = r.data?.stats;
      if (Array.isArray(s) && s.length > 0) setStats(s);
    }).catch(() => {});
  }, []);

  // Pinned services first, then hot, then by order. Limit to 6 for preview.
  const sortedServices = useMemo(() => {
    const pinned = services.filter((s) => s.pinned);
    const rest = services.filter((s) => !s.pinned);
    if (hotTitle) {
      const hotIdx = rest.findIndex((s) => s.title === hotTitle);
      if (hotIdx >= 0) {
        const [hot] = rest.splice(hotIdx, 1);
        return [...pinned, hot, ...rest].slice(0, 6);
      }
    }
    return [...pinned, ...rest].slice(0, 6);
  }, [services, hotTitle]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Zomato & Swiggy Profile Optimization for Restaurants"
        description="More customers. Better orders. Higher menu scores. We optimize hotel & restaurant profiles on Zomato and Swiggy — menu design, food photography, brand identity."
        path="/"
      />
      {/* Hero */}
      <section className="border-b border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#525252] mb-6">
              <span className="h-px w-8 bg-[var(--brand-primary)]" /> For Zomato & Swiggy
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-black">
              More customers.<br />
              <span className="text-[var(--brand-primary)]">Better orders.</span><br />
              Higher menu scores.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#525252] leading-relaxed">
              We optimize hotel & restaurant profiles on Zomato and Swiggy with proven strategies — profile tuning, menu design, food photography, and brand identity.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] px-8 py-4 rounded-sm transition-colors font-medium inline-flex items-center gap-2"
              >
                Get a Free Audit <ArrowUpRight size={18} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="bg-white text-[#0A0A0A] border border-black/10 hover:bg-[#FAFAFA] px-8 py-4 rounded-sm transition-colors"
              >
                Explore Services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 bg-[var(--brand-primary)]/5 rounded-sm" />
              <img
                src={HERO_IMG}
                alt="Restaurant Zomato profile"
                className="relative w-full h-[480px] object-cover rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-display text-4xl sm:text-5xl tracking-tighter font-bold">{s.value}</div>
              <div className="text-sm text-white/50 mt-2 uppercase tracking-[0.15em]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Hero banner ad slot */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-12">
        <AdSlot page="home" slot="hero-banner" />
      </div>

      {/* Services preview - Tetris Grid + Sidebar ad */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-3">What we do</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold max-w-2xl">
                Six services designed to grow your <span className="text-[var(--brand-primary)]">restaurant</span>.
              </h2>
            </div>
            <Link to="/services" className="text-sm font-medium inline-flex items-center gap-1 hover:text-[var(--brand-primary)]" data-testid="home-view-all-services">
              View all <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className={hasSidebarAds ? "lg:col-span-9" : "lg:col-span-12"}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {sortedServices.map((s, i) => {
                  const Ic = iconMap[s.icon] || Sparkles;
                  const isHot = hotTitle && s.title === hotTitle;
                  const isPinned = !!s.pinned;
                  const highlighted = isHot || isPinned;
                  // Tetris pattern adjusted for 9-col container: pos 0->12, 1->6, 2->6, 3->6, 4->6, 5->12
                  const spans = ["md:col-span-12", "md:col-span-6", "md:col-span-6", "md:col-span-6", "md:col-span-6", "md:col-span-12"];
                  return (
                    <Link
                      key={s.id}
                      to="/services"
                      data-testid={`home-service-card-${s.slug}`}
                      className={`relative group bg-white border p-8 transition-all ${spans[i]} block card-reveal card-reveal-${i + 1} ${
                        highlighted && i === 0 ? "border-[var(--brand-primary)]/40 ring-1 ring-[var(--brand-primary)]/20" : "border-black/5 hover:border-[var(--brand-primary)]/30"
                      }`}
                    >
                      {highlighted && i === 0 && (
                        <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm" data-testid="home-hot-badge">
                          {isPinned ? <><Pin size={12} strokeWidth={2.2} /> Pinned</> : <><Flame size={12} strokeWidth={2.2} /> Most Requested</>}
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-8">
                        <Ic strokeWidth={1.5} className="text-[var(--brand-primary)]" size={28} />
                        <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                      </div>
                      <h3 className="font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
                      <p className="mt-3 text-[#525252] text-sm leading-relaxed">{s.short_description}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
            {hasSidebarAds && (
              <aside className="lg:col-span-3" data-testid="home-sidebar-ads">
                <div className="lg:sticky lg:top-24">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-2">Sponsored</div>
                  <AdSlot page="home" slot="sidebar" />
                </div>
              </aside>
            )}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white border-y border-black/5 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-3">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold mb-12">A simple 3-step process.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n: "01", t: "Free Audit", d: "Share your Zomato/Swiggy link. We analyze your profile, menu, photos and ranking." },
              { n: "02", t: "Action Plan", d: "We send you a tailored plan with everything we'll do to grow your orders." },
              { n: "03", t: "Execute & Grow", d: "Our team executes within 48 hours. You see results in days, not months." },
            ].map((p) => (
              <div key={p.n} className="border-l-2 border-[var(--brand-primary)] pl-6">
                <div className="font-mono text-sm text-[var(--brand-primary)]">{p.n}</div>
                <div className="font-display text-2xl font-semibold mt-2">{p.t}</div>
                <p className="text-[#525252] mt-2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work */}
      {featured.length > 0 && (
        <section className="py-20 lg:py-28 bg-white border-t border-black/5" data-testid="home-featured-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
              <div>
                <div className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-3">
                  <Star size={14} strokeWidth={2} /> Featured Work
                </div>
                <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold max-w-2xl">
                  Real restaurants, <span className="text-[var(--brand-primary)]">real transformations</span>.
                </h2>
              </div>
              <Link
                to="/portfolio"
                data-testid="home-view-all-portfolio"
                className="text-sm font-medium inline-flex items-center gap-1 hover:text-[var(--brand-primary)]"
              >
                View all <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((p) => (
                <div
                  key={p.id}
                  data-testid={`home-featured-item-${p.id}`}
                  className="bg-white border border-black/5 overflow-hidden group hover:border-[var(--brand-primary)]/30 transition-colors"
                >
                  <div className="grid grid-cols-2 h-56">
                    <div className="relative border-r border-black/10 overflow-hidden bg-[#F0F0F0]">
                      <div className="absolute top-3 left-3 z-10 bg-white/90 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">Before</div>
                      {p.before_image_id && (
                        <img src={fileUrl(p.before_image_id)} alt="before" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                      )}
                    </div>
                    <div className="relative overflow-hidden bg-[#F0F0F0]">
                      <div className="absolute top-3 left-3 z-10 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">After</div>
                      {p.after_image_id && (
                        <img src={fileUrl(p.after_image_id)} alt="after" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)]">{p.category}</div>
                    <h3 className="mt-2 font-display text-xl font-semibold">{p.title}</h3>
                    {p.description && (
                      <p className="mt-2 text-sm text-[#525252] leading-relaxed line-clamp-2">{p.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-black leading-tight">
              Ready to attract <span className="text-[var(--brand-primary)]">more customers</span>?
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              Book your free audit today. We'll show you exactly what's holding your restaurant back online.
            </p>
            <Link
              to="/contact"
              data-testid="home-bottom-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] px-8 py-4 rounded-sm font-medium"
            >
              Start now <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className="space-y-4">
            {["Zomato & Swiggy specialists", "48-hour turnaround", "No long contracts", "Pay only when satisfied"].map((b) => (
              <div key={b} className="flex items-center gap-3 text-white/80">
                <CheckCircle2 size={18} className="text-[var(--brand-primary)]" />
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
